import React, {FC} from 'react';
import styled from 'styled-components';

import {FrontClickEvent} from '../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {isDefined} from '../../../../../libs/shared/core/src/helpers/typescript/typeHelpers';
import {MessageTypesEnum} from '../../../../../libs/shared/core/src/models/api/commonMessageModel';
import {
  isConversationInTeamOrAssigneeContext,
  isInternalConversation,
  UnreadStatusesEnum,
} from '../../../../helpers/models/conversationHelpers';
import {CellActivity} from '../content/cell/cellActivity';
import {CellAttachmentIcon} from '../content/cell/cellAttachmentIcon';
import {CellDate} from '../content/cell/cellDate';
import {CellHoverIconsContainer} from '../content/cell/cellHoverIconsContainer';
import {CellHoverIconsTicketStatus} from '../content/cell/cellHoverIconsTicketStatus';
import {CellInboxColor} from '../content/cell/cellInboxColor';
import {CellInternalIcon} from '../content/cell/cellInternalIcon';
import {CellMiddle} from '../content/cell/cellMiddle';
import {CellOverlay} from '../content/cell/cellOverlay';
import {CellRecipient} from '../content/cell/cellRecipient';
import {CellRecipientSenders} from '../content/cell/cellRecipientSenders';
import {CellStatus} from '../content/cell/cellStatus';
import {CellStatusIcon} from '../content/cell/cellStatusIcon';
import {CellTeam} from '../content/cell/cellTeam';
import {ConversationCellCommonProps} from '../conversationCellCommon';

/*
 * Props.
 */

interface ConversationCellContentProps extends ConversationCellCommonProps {
  onToggleMarkAsRead: () => void;
  /** Whether this particular cell will display quick actions when the cell is hovered. */
  shouldShowQuickActionsOnCellHover: boolean;
  onSnoozeClick: (event: FrontClickEvent) => void;
  isSnoozeDropdownOpen: boolean;
  onSnoozeDropdownClose: () => void;
  isTicketStatusDropdownOpen: boolean;
  onTicketStatusDropdownToggle: (isOpen: boolean) => void;
  onOpenClick: (event: FrontClickEvent) => void;
  onTrashClick: (event: FrontClickEvent) => void;
  isCompact?: boolean;
}

/*
 * Style.
 */

// We wrap the component so that we can target it in a selector below.
const StyledCellAttachmentRightIcon = styled(CellAttachmentIcon)``;

const StyledBottomRightWrapperDiv = styled.div`
  grid-area: bottom-right;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  & > div:nth-child(2) {
    margin-left: 8px;
  }
`;

const StyledTopLineWrapperDiv = styled.div`
  grid-area: top-line;
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 0;
  & > div:nth-child(2) {
    margin-left: 6px;
  }
`;

const StyledCellStatusIcon = styled(CellStatusIcon)`
  grid-area: top-right;
`;

/*
 * Component.
 */

export const ConversationCellContent: FC<ConversationCellContentProps> = (props) => (
  <>
    {renderTopLine(props)}
    {renderMiddleLine(props)}
    {renderBottomLine(props)}
    {renderAbsolute(props)}
  </>
);

/*
 * Helpers.
 */

function renderTopLine(props: ConversationCellContentProps) {
  const {
    conversation,
    conversationHighlight,
    isSelected,
    status,
    ticketStatus,
    unreadStatus,
    reminderDate,
    cellsContext,
    conversationSenders,
    userId,
    shouldConsiderInPrivateTag,
    onToggleMarkAsRead,
    shouldShowQuickActionsOnCellHover,
    onSnoozeClick,
    isSnoozeDropdownOpen,
    onSnoozeDropdownClose,
    onTrashClick,
    onOpenClick,
    shouldRenderConversationSenders,
    isTicketStatusDropdownOpen,
    onTicketStatusDropdownToggle,
  } = props;

  return (
    <>
      <CellStatus
        status={props.status}
        unreadStatus={props.unreadStatus}
        isSelected={props.isSelected}
        toggleMarkAsRead={onToggleMarkAsRead}
      />
      <StyledTopLineWrapperDiv>
        {shouldRenderConversationSenders && conversationSenders ? (
          <CellRecipientSenders
            conversation={props.conversation}
            conversationSenders={conversationSenders}
            userId={userId}
            assignee={props.assignee}
            isSelected={props.isSelected}
            isUnread={props.unreadStatus !== UnreadStatusesEnum.NONE}
            isDiscussion={props.conversation.messageType === MessageTypesEnum.INTERNAL}
            trackerTeammates={props.trackerTeammates}
            isUserTracking={isDefined(props.trackerStatus)}
            conversationHighlight={props.conversationHighlight}
          />
        ) : (
          <CellRecipient
            conversation={props.conversation}
            recipientHighlight={conversationHighlight && conversationHighlight.recipient}
            trackerTeammates={props.trackerTeammates}
            userId={userId}
            recipient={props.recipient}
            assignee={props.assignee}
            inbox={props.inbox}
            lastCommentAuthor={props.lastComment?.author}
            isSelected={props.isSelected}
            isUnread={props.unreadStatus !== UnreadStatusesEnum.NONE}
          />
        )}
        <CellDate
          date={props.date}
          reminderDate={props.reminderDate}
          slaTimerDate={props.slaTimerDate}
          waitingSinceMs={conversation.orders.waiting_since ?? 0}
          isSelected={isSelected}
        />
      </StyledTopLineWrapperDiv>
      <StyledCellStatusIcon
        isSelected={isSelected}
        unreadStatus={unreadStatus}
        status={status}
        ticketStatusCategory={ticketStatus?.category}
        reminderDate={reminderDate}
      />
      {shouldShowQuickActionsOnCellHover && ticketStatus === undefined && (
        <CellHoverIconsContainer
          cellsContext={cellsContext}
          onTrashClick={onTrashClick}
          onOpenClick={onOpenClick}
          isSelected={isSelected}
          status={status}
          conversation={conversation}
          hasReminder={Boolean(reminderDate)}
          isTeamContext={isConversationInTeamOrAssigneeContext(cellsContext, conversation)}
          isDelegated={cellsContext.isDelegation}
          onSnoozeClick={onSnoozeClick}
          isSnoozeDropdownOpen={isSnoozeDropdownOpen}
          onSnoozeDropdownClose={onSnoozeDropdownClose}
          shouldConsiderInPrivateTag={Boolean(shouldConsiderInPrivateTag)}
        />
      )}
      {shouldShowQuickActionsOnCellHover && ticketStatus !== undefined && (
        <CellHoverIconsTicketStatus
          isCompact={false}
          conversation={conversation}
          cellsContext={cellsContext}
          isTicketStatusDropdownOpen={isTicketStatusDropdownOpen}
          onTicketStatusDropdownToggle={onTicketStatusDropdownToggle}
        />
      )}
    </>
  );
}

function renderMiddleLine(props: ConversationCellContentProps) {
  const {
    conversation,
    conversationHighlight,
    draft,
    isSelected,
    numMessages,
    externalChannelType,
    applicationChannelType,
    ticketStatus,
    channelTypes,
  } = props;
  const {messageType} = conversation;

  const conversationMessageType = (numMessages === 0 && draft && draft.type) || messageType;
  const draftSubject = draft && draft.subject;
  const subject = draftSubject || conversation.subject || '';

  return (
    <>
      {isInternalConversation(conversation) && <CellInternalIcon isSelected={isSelected} />}
      <CellMiddle
        messageType={conversationMessageType}
        subject={subject}
        subjectHighlight={conversationHighlight && conversationHighlight.subject}
        recipient={props.recipient}
        tags={props.tags}
        badgeValue={numMessages ?? 0}
        isSelected={props.isSelected}
        isStarred={props.isStarred}
        externalChannelType={externalChannelType}
        applicationChannelType={applicationChannelType}
        isOriginalConversation={props.isOriginalConversation}
        numLinkedConversations={props.conversation.numLinkedConversations}
        conversation={conversation}
        ticketStatus={ticketStatus}
        channelTypes={channelTypes}
      />
    </>
  );
}

function renderBottomLine(props: ConversationCellContentProps) {
  return (
    <>
      <CellActivity
        conversationId={props.conversation.id}
        mentionableAliases={props.mentionableAliases}
        summary={props.conversation.summary}
        conversationHighlight={props.conversationHighlight}
        searchActivity={props.searchActivity}
        commentingTeammate={props.commentingTeammate}
        replyingTeammate={props.replyingTeammate}
        draftSeedAuthors={props.draftSeedAuthors}
        lastManualMessage={props.lastManualMessage}
        lastComment={props.lastComment}
        lastCommentDraft={props.lastCommentDraft}
        commentDraftSummary={props.commentDraftSummary}
        isSelected={props.isSelected}
        draft={props.draft}
        bumpActivity={props.bumpActivity}
        bumpComment={props.bumpComment}
        bumpTeammateSource={props.bumpTeammateSource}
        unreadStatus={props.unreadStatus}
        searchResultCommentSummary={props.searchResultCommentSummary}
        isInDraftsFolder={false}
        teammateId={props.cellsContext.teammateId}
        isCompact={props.isCompact}
      />
      <StyledBottomRightWrapperDiv>
        {props.conversation.hasAttachments && <StyledCellAttachmentRightIcon isSelected={props.isSelected} />}
        {props.team && <CellTeam team={props.team} />}
      </StyledBottomRightWrapperDiv>
    </>
  );
}

/** Render absolute positioned items. */
function renderAbsolute(props: ConversationCellContentProps) {
  const {inbox, overlay, isSelected} = props;

  return (
    <>
      {inbox && <CellInboxColor inbox={inbox} />}
      {overlay && (
        <CellOverlay
          isSelected={isSelected}
          ticketStatus={overlay.ticketStatus}
          type={overlay.type}
          requester={overlay.requester}
          assignee={overlay.assignee}
          inbox={overlay.inbox}
          conversationId={overlay.conversationId}
        />
      )}
    </>
  );
}
